import React, { useState, useEffect } from "react";
import PageBanner from "../../components/common/PageBanner";
import BBQImage from "../../Assets/Images/BarRestaurants/faith-menu.webp";
import FullMenuImage from "../../Assets/Images/BarRestaurants/full-menu.webp";
import BigScreenImage from "../../Assets/Images/BarRestaurants/image 1972.webp";
import EatDrinkPlayImage from "../../Assets/Images/BarRestaurants/image 1973.webp";
import axios from "axios";
import AOS from "aos";
import "aos/dist/aos.css";

const BarAndRestaurant = () => {
  const [modalImage, setModalImage] = useState(null);
  const [bannerData, setBannerData] = useState({});
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch banner data
  const getBannerData = async () => {
    try {
      let config = {
        method: "get",
        maxBodyLength: Infinity,
        url: `${process.env.REACT_APP_API_URL}/page-banner/get-banner/bar-restaurant`,
        headers: {},
      };

      const response = await axios.request(config);
      setBannerData(response.data);
    } catch (error) {
      console.error("Error fetching banner data:", error);
    }
  };

  // Fetch page content data
  const getPageContent = async () => {
    try {
      setLoading(true);
      let config = {
        method: "get",
        maxBodyLength: Infinity,
        url: `${process.env.REACT_APP_API_URL}/bar-restaurants-contents/get-contents`,
        headers: {},
      };

      const response = await axios.request(config);
      setPageData(response.data);
    } catch (error) {
      console.error("Error fetching page content:", error);
      setError("Failed to load page content");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    AOS.init({
      duration: 800,
      once: false,
      mirror: true,
      easing: "ease-out",
    });

    const handleScroll = () => {
      AOS.refresh();
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    getBannerData();
    getPageContent();
  }, []);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primaryBtnColor border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-primaryTextColor">Loading...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="text-6xl mb-4">😞</div>
          <h3 className="text-xl font-bold text-primaryTextColor mb-2">
            Oops!
          </h3>
          <p className="text-secondaryTextColor">{error}</p>
          <button
            onClick={getPageContent}
            className="mt-4 px-6 py-2 bg-primaryBtnColor text-white rounded-lg hover:bg-primaryBtnHoverColor transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // If no data is available
  if (!pageData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="text-6xl mb-4">📺</div>
          <h3 className="text-xl font-bold text-primaryTextColor mb-2">
            No Data Available
          </h3>
          <p className="text-secondaryTextColor">Please check back later.</p>
        </div>
      </div>
    );
  }

  const { hero, fuelUpSection, eatDrinkPlay, bigScreen, menus, featureIcons } =
    pageData;

  return (
    <div className="bg-white">
      <PageBanner
        title="EAT & DRINK"
        subtitle="Coming Soon - The Dugout Bar & Grill"
        background={bannerData.bgImage}
      />

      <div className="tracking-wide mb-12 font-subheading min-h-screen bg-white text-primaryTextColor overflow-hidden">
        {/* Hero Section */}
        <div className="relative pt-12 px-4 md:px-8 lg:px-12">
          <div className="">
            {/* Header */}
            <div className="text-center mb-12">
              <h1
                className="font-heading text-3xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-primaryTextColor to-primaryBtnHoverColor bg-clip-text text-transparent"
                data-aos="zoom-in"
              >
                {hero?.title && !hero.title.toLowerCase().includes("banana")
                  ? hero.title
                  : "THE DUGOUT BAR & GRILL"}
              </h1>
              <p
                className="text-xl md:text-2xl text-secondaryTextColor max-w-3xl mx-auto"
                data-aos="zoom-in"
              >
                {hero?.subtitle && !hero.subtitle.toLowerCase().includes("silverbacks")
                  ? hero.subtitle
                  : "Coming Soon — The Ultimate Sports Dining Experience at Strikes & Shots"}
              </p>
            </div>

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
              {/* Fuel Up Section */}
              <div className="lg:col-span-2" data-aos="fade-right">
                <div className="bg-gradient-to-br from-primaryBtnColor to-primaryTextColor rounded-3xl p-8 text-white shadow-2xl h-full">
                  {/* Header */}
                  <div className="flex items-start mb-6">
                    <div className="w-16 h-16 bg-white bg-opacity-20 rounded-2xl flex items-center justify-center mr-6 backdrop-blur-sm">
                      <span className="text-3xl">⚡</span>
                    </div>
                    <div>
                      <h2 className="text-4xl font-bold font-heading mb-2">
                        {fuelUpSection?.title || "Fuel Up & Cool Down"}
                      </h2>
                      <div className="w-20 h-1 bg-primaryBtnHoverColor rounded-full"></div>
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-xl text-gray-100 mb-8 leading-relaxed">
                    {fuelUpSection?.description ||
                      "Fuel up for your upcoming games with our delicious food, and cool off after your victory with an ice-cold beer from the tap."}
                  </p>

                  {/* Features / Highlights */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
                    {fuelUpSection?.highlights?.map((highlight, index) => (
                      <div
                        key={highlight._id || index}
                        className="flex items-start space-x-4"
                      >
                        <div className="text-3xl">{highlight.icon}</div>
                        <div>
                          <h3 className="text-2xl font-bold">
                            {highlight.title}
                          </h3>
                          <p className="text-gray-100">
                            {highlight.description}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Bullet Points */}
                  {fuelUpSection?.bulletPoints && (
                    <ul className="list-disc list-inside space-y-2 text-gray-200 text-lg mb-6">
                      {fuelUpSection.bulletPoints.map((point, index) => (
                        <li key={index}>{point}</li>
                      ))}
                    </ul>
                  )}

                  {/* Note */}
                  {fuelUpSection?.note && (
                    <p className="text-gray-200 text-sm">
                      {fuelUpSection.note}
                    </p>
                  )}
                </div>
              </div>

              {/* Eat Drink Play Section */}
              <div
                className="bg-white rounded-3xl p-8 border-2 border-gray-100 shadow-xl h-full overflow-hidden"
                data-aos="fade-left"
              >
                <div className="flex flex-col h-full">
                  {/* Image Section */}
                  <div className="relative h-48 mb-6 rounded-2xl overflow-hidden group">
                    <img
                      src={eatDrinkPlay?.image || EatDrinkPlayImage}
                      alt="Eat Drink Play Experience"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                    <div className="absolute bottom-4 left-4">
                      <span className="text-white text-sm font-semibold bg-primaryBtnHoverColor px-3 py-1 rounded-full">
                        🎯 EXPERIENCE
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center mb-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-primaryBtnColor to-primaryBtnHoverColor rounded-2xl flex items-center justify-center mr-6">
                      <span className="text-3xl text-white">🎯</span>
                    </div>
                    <h2 className="text-3xl font-bold font-heading text-primaryTextColor">
                      {eatDrinkPlay?.title || "EAT, DRINK, PLAY!"}
                    </h2>
                  </div>
                  <p className="text-lg text-secondaryTextColor leading-relaxed flex-grow">
                    {eatDrinkPlay?.description ||
                      "Our full-service restaurant and bar is the best place to sit back and relax before and after your games here at Silverbacks Indoor. With a wide variety of foods and beverages, we cater to everyone that walks through our doors."}
                  </p>
                  <div className="mt-6 flex flex-wrap gap-3">
                    {eatDrinkPlay?.tags?.map((tag, index) => (
                      <span
                        key={index}
                        className="px-4 py-2 bg-gray-50 text-primaryTextColor rounded-full text-sm font-semibold"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Big Screen Section */}
              <div className="lg:col-span-3" data-aos="fade-up">
                <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                  {/* Background Image with Overlay */}
                  <div className="absolute inset-0">
                    <img
                      src={BigScreenImage}
                      alt="Big Screen Sports Viewing"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-primaryBtnColor/90 to-primaryBtnHoverColor/80"></div>
                  </div>

                  {/* Content */}
                  <div className="relative z-10 p-8 lg:p-12 text-white">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                      {/* Text Content */}
                      <div>
                        <div className="flex items-center mb-6">
                          <div className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center mr-6 backdrop-blur-sm border border-white/30">
                            <span className="text-4xl">📺</span>
                          </div>
                          <div>
                            <h2 className="text-3xl lg:text-4xl font-bold font-heading mb-2">
                              {bigScreen?.title || "WATCH ON THE"}
                              <br />
                              <span className="bg-gradient-to-r from-white to-amber-200 bg-clip-text text-transparent">
                                {bigScreen?.subtitle || "BIG SCREEN!"}
                              </span>
                            </h2>
                            <div className="w-24 h-1 bg-amber-400 rounded-full"></div>
                          </div>
                        </div>

                        <p className="text-xl text-gray-100 mb-8 leading-relaxed">
                          {bigScreen?.description &&
                          !bigScreen.description.toLowerCase().includes("silverbacks")
                            ? bigScreen.description
                            : "The Dugout Bar & Grill hosts a variety of events at Strikes & Shots, including watch-parties and much more. When we say big screen, we really mean it. Come and enjoy your favorite sports team make its way to the top."}
                        </p>

                        {/* Features Grid */}
                        <div className="grid grid-cols-3 gap-3 mb-6 max-w-full">
                          {bigScreen?.features?.map((feature, index) => (
                            <div
                              key={feature._id || index}
                              className="text-center bg-white/10 rounded-xl p-3 sm:p-4 backdrop-blur-sm border border-white/20 flex flex-col items-center justify-center"
                            >
                              <div className="text-base sm:text-xl md:text-2xl font-bold text-amber-300 leading-tight">
                                {feature.value}
                              </div>
                              <div className="text-gray-200 text-xs sm:text-sm md:text-base font-semibold leading-tight whitespace-normal">
                                {feature.label.toUpperCase()}
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* CTA Button */}
                        <button className="inline-flex items-center px-8 py-4 bg-white text-primaryBtnColor font-heading font-bold rounded-2xl hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl">
                          BOOK WATCH PARTY
                          <span className="ml-3 text-lg">🎉</span>
                        </button>
                      </div>

                      {/* Stats Card */}
                      <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
                        <h3 className="text-2xl font-bold text-center mb-6 text-amber-300">
                          PREMIUM EXPERIENCE
                        </h3>

                        <div className="space-y-4">
                          {bigScreen?.stats?.map((stat, index) => (
                            <div
                              key={stat._id || index}
                              className="flex items-center justify-between p-3 bg-white/5 rounded-lg"
                            >
                              <span className="text-gray-200">
                                {stat.label}
                              </span>
                              <span className="font-bold text-amber-300">
                                {stat.value}
                              </span>
                            </div>
                          ))}
                        </div>

                        {/* Badge */}
                        <div className="mt-6 text-center">
                          <div className="inline-flex items-center px-4 py-2 bg-amber-500 text-white rounded-full text-sm font-bold">
                            ⭐ ULTIMATE VIEWING EXPERIENCE
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Bottom Features */}
                    <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                      {bigScreen?.bottomFeatures?.map((feature, index) => (
                        <div
                          key={feature._id || index}
                          className="bg-white/5 rounded-lg p-3 backdrop-blur-sm border border-white/10"
                        >
                          <div className="text-amber-300 text-lg">
                            {feature.icon}
                          </div>
                          <div className="text-white text-sm font-semibold">
                            {feature.label}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Menu Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Faith Barbeque Menu */}
              <div className="group relative" data-aos="fade-right">
                <div className="relative bg-gradient-to-br from-primaryBtnColor to-primaryTextColor rounded-3xl p-1 shadow-2xl overflow-hidden">
                  {/* Background Pattern */}
                  <div className="absolute inset-0 opacity-10">
                    <div
                      className="absolute inset-0 bg-repeat"
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.2'%3E%3Cpath d='M0 0h40v40H0V0zm20 20h20v20H20V20zm0-20h20v20H20V0z'/%3E%3C/g%3E%3C/svg%3E")`,
                      }}
                    ></div>
                  </div>

                  <div className="relative bg-white rounded-2xl overflow-hidden group-hover:scale-[1.02] transition-all duration-500">
                    {/* Image Container */}
                    <div className="relative h-56 overflow-hidden">
                      <img
                        src={menus?.bbqMenu?.image || BBQImage}
                        alt={menus?.bbqMenu?.title || "Faith Barbeque Menu"}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      />
                      {/* Gradient Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-primaryBtnColor/80 to-transparent"></div>
                      {/* Badge */}
                      <div className="absolute top-4 left-4">
                        <span className="bg-primaryBtnHoverColor text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg">
                          🔥 SIGNATURE BBQ
                        </span>
                      </div>
                      {/* Icon */}
                      <div className="absolute top-4 right-4 w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                        <span className="text-2xl">🍖</span>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-6 text-center">
                      <h3 className="text-2xl lg:text-3xl font-bold font-heading text-primaryTextColor mb-3">
                        {menus?.bbqMenu?.title || "Faith Barbeque"}
                      </h3>
                      <p className="text-secondaryTextColor mb-4 leading-relaxed">
                        {menus?.bbqMenu?.subtitle ||
                          "Slow-smoked specialties & mouth-watering grilled favorites"}
                      </p>

                      {/* Features */}
                      {menus?.bbqMenu?.features && (
                        <div className="flex justify-center flex-wrap gap-2 mb-6">
                          {menus.bbqMenu.features.map((feature, index) => (
                            <span
                              key={index}
                              className="text-xs bg-amber-100 text-amber-800 px-3 py-1 rounded-full font-semibold"
                            >
                              {feature}
                            </span>
                          ))}
                        </div>
                      )}

                      <button
                        onClick={() =>
                          setModalImage(menus?.bbqMenu?.image || BBQImage)
                        }
                        className="w-full py-4 bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor text-white font-heading font-bold rounded-xl hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl relative overflow-hidden group"
                      >
                        <span className="relative z-10">VIEW BBQ MENU</span>
                        <div className="absolute inset-0 bg-gradient-to-r from-primaryBtnHoverColor to-red-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Full Menu */}
              <div className="group relative" data-aos="fade-left">
                <div className="relative bg-gradient-to-br from-primaryBtnHoverColor to-red-600 rounded-3xl p-1 shadow-2xl overflow-hidden">
                  {/* Background Pattern */}
                  <div className="absolute inset-0 opacity-10">
                    <div
                      className="absolute inset-0 bg-repeat"
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.2'%3E%3Cpath d='M0 0h40v40H0V0zm20 20h20v20H20V20zm0-20h20v20H20V0z'/%3E%3C/g%3E%3C/svg%3E")`,
                      }}
                    ></div>
                  </div>

                  <div className="relative bg-white rounded-2xl overflow-hidden group-hover:scale-[1.02] transition-all duration-500">
                    {/* Image Container */}
                    <div className="relative h-56 overflow-hidden">
                      <img
                        src={menus?.fullMenu?.image || FullMenuImage}
                        alt={menus?.fullMenu?.title || "Full Menu"}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      />
                      {/* Gradient Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-primaryBtnHoverColor/80 to-transparent"></div>
                      {/* Badge */}
                      <div className="absolute top-4 left-4">
                        <span className="bg-primaryBtnColor text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg">
                          📚 COMPLETE MENU
                        </span>
                      </div>
                      {/* Icon */}
                      <div className="absolute top-4 right-4 w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                        <span className="text-2xl">🍽️</span>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-6 text-center">
                      <h3 className="text-2xl lg:text-3xl font-bold font-heading text-primaryTextColor mb-3">
                        {menus?.fullMenu?.title || "Full Menu"}
                      </h3>
                      <p className="text-secondaryTextColor mb-4 leading-relaxed">
                        {menus?.fullMenu?.subtitle ||
                          "Complete selection of food, drinks, and daily specials"}
                      </p>

                      {/* Features */}
                      {menus?.fullMenu?.features && (
                        <div className="flex justify-center flex-wrap gap-2 mb-6">
                          {menus.fullMenu.features.map((feature, index) => (
                            <span
                              key={index}
                              className="text-xs bg-red-100 text-red-800 px-3 py-1 rounded-full font-semibold"
                            >
                              {feature}
                            </span>
                          ))}
                        </div>
                      )}

                      <button
                        onClick={() =>
                          setModalImage(menus?.fullMenu?.image || FullMenuImage)
                        }
                        className="w-full py-4 bg-gradient-to-r from-primaryBtnHoverColor to-red-600 text-white font-heading font-bold rounded-xl hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl relative overflow-hidden group"
                      >
                        <span className="relative z-10">VIEW FULL MENU</span>
                        <div className="absolute inset-0 bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal */}
            {modalImage && (
              <div
                onClick={() => setModalImage(null)}
                className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-50 cursor-pointer p-4"
              >
                <div className="relative max-w-4xl max-h-[90vh]">
                  <img
                    src={modalImage}
                    alt="Menu"
                    className="max-h-[85vh] max-w-full rounded-2xl shadow-2xl object-contain"
                  />
                  {/* Close Button */}
                  <button
                    onClick={() => setModalImage(null)}
                    className="absolute -top-4 -right-4 w-12 h-12 bg-primaryBtnHoverColor text-white rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform duration-200"
                  >
                    ✕
                  </button>
                  {/* Download Hint */}
                  <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/50 text-white px-4 py-2 rounded-full text-sm backdrop-blur-sm">
                    Click anywhere to close
                  </div>
                </div>
              </div>
            )}

            {/* Feature Icons */}
            {featureIcons && featureIcons.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-20 max-w-5xl mx-auto">
                {featureIcons.map((feature, index) => (
                  <div key={feature._id || index} className="text-center">
                    <div className="w-16 h-16 bg-primaryBtnHoverColor rounded-full flex items-center justify-center mx-auto mb-3">
                      <span className="text-2xl text-white">
                        {feature.icon}
                      </span>
                    </div>
                    <p className="font-semibold text-primaryTextColor">
                      {feature.label}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BarAndRestaurant;

// import React, { useState, useEffect } from "react";
// import PageBanner from "../../components/common/PageBanner";
// import BgImage from "../../Assets/Images/BarRestaurants/barHero.webp";
// import BBQImage from "../../Assets/Images/BarRestaurants/faith-menu.webp";
// import FullMenuImage from "../../Assets/Images/BarRestaurants/full-menu.webp";
// import BigScreenImage from "../../Assets/Images/BarRestaurants/image 1972.webp";
// import EatDrinkPlayImage from "../../Assets/Images/BarRestaurants/image 1973.webp";
// import axios from "axios";
// import AOS from "aos";
// import "aos/dist/aos.css";

// const BarAndRestaurant = () => {
//   const [modalImage, setModalImage] = useState(null);
//   const [bannerData, setBannerData] = useState({});

//   const getBannerData = () => {
//     let config = {
//       method: "get",
//       maxBodyLength: Infinity,
//       url: `${process.env.REACT_APP_API_URL}/page-banner/get-banner/bar-restaurant`,
//       headers: {},
//     };

//     axios
//       .request(config)
//       .then((response) => {
//         console.log(JSON.stringify(response.data));
//         setBannerData(response.data);
//       })
//       .catch((error) => {
//         console.log(error);
//       });
//   };

//   useEffect(() => {
//     AOS.init({
//       duration: 800,
//       once: false, // re-trigger every time visible
//       mirror: true, // animation works when scrolling back up too
//       easing: "ease-out",
//     });

//     const handleScroll = () => {
//       AOS.refresh(); // refresh animations on scroll
//     };

//     window.addEventListener("scroll", handleScroll);
//     return () => window.removeEventListener("scroll", handleScroll);
//   }, []);

//   useEffect(() => {
//     getBannerData();
//   }, []);

//   return (
//     <div className="bg-white">
//       <PageBanner
//         title={bannerData.title}
//         subtitle={bannerData.subtitle}
//         background={BgImage}
//       />

//       <div className="tracking-wide mb-12 font-subheading min-h-screen bg-white text-primaryTextColor overflow-hidden">
//         {/* Hero Section */}
//         <div className="relative pt-12 px-4 md:px-8 lg:px-12">
//           <div className="">
//             {/* Header */}
//             <div className="text-center mb-12">
//               <h1
//                 className="font-heading text-3xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-primaryTextColor to-primaryBtnHoverColor bg-clip-text text-transparent"
//                 data-aos="zoom-in"
//               >
//                 BANANA'S BAR & RESTAURANT
//               </h1>
//               <p
//                 className="text-xl md:text-2xl text-secondaryTextColor max-w-3xl mx-auto"
//                 data-aos="zoom-in"
//               >
//                 The Ultimate Sports Dining Experience at Silverbacks Indoor
//               </p>
//             </div>

//             {/* Main Content Grid */}
//             <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
//               {/* Fuel Up Section */}
//               <div className="lg:col-span-2" data-aos="fade-right">
//                 <div className="bg-gradient-to-br from-primaryBtnColor to-primaryTextColor rounded-3xl p-8 text-white shadow-2xl h-full">
//                   {/* Header */}
//                   <div className="flex items-start mb-6">
//                     <div className="w-16 h-16 bg-white bg-opacity-20 rounded-2xl flex items-center justify-center mr-6 backdrop-blur-sm">
//                       <span className="text-3xl">⚡</span>
//                     </div>
//                     <div>
//                       <h2 className="text-4xl font-bold font-heading mb-2">
//                         Fuel Up & Cool Down
//                       </h2>
//                       <div className="w-20 h-1 bg-primaryBtnHoverColor rounded-full"></div>
//                     </div>
//                   </div>

//                   {/* Description */}
//                   <p className="text-xl text-gray-100 mb-8 leading-relaxed">
//                     Fuel up for your upcoming games with our delicious food, and
//                     cool off after your victory with an ice-cold beer from the
//                     tap.
//                   </p>

//                   {/* Features / Highlights */}
//                   <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
//                     <div className="flex items-start space-x-4">
//                       <div className="text-3xl">🍔</div>
//                       <div>
//                         <h3 className="text-2xl font-bold">
//                           Signature Burgers
//                         </h3>
//                         <p className="text-gray-100">
//                           Juicy, mouth-watering patties with fresh ingredients.
//                         </p>
//                       </div>
//                     </div>
//                     <div className="flex items-start space-x-4">
//                       <div className="text-3xl">🍺</div>
//                       <div>
//                         <h3 className="text-2xl font-bold">
//                           Craft Beer on Tap
//                         </h3>
//                         <p className="text-gray-100">
//                           Refresh yourself with our hand-picked craft beers.
//                         </p>
//                       </div>
//                     </div>
//                     <div className="flex items-start space-x-4">
//                       <div className="text-3xl">🔥</div>
//                       <div>
//                         <h3 className="text-2xl font-bold">Smoked Specials</h3>
//                         <p className="text-gray-100">
//                           Slow-cooked meats that melt in your mouth.
//                         </p>
//                       </div>
//                     </div>
//                     <div className="flex items-start space-x-4">
//                       <div className="text-3xl">❄️</div>
//                       <div>
//                         <h3 className="text-2xl font-bold">Ice-Cold Drinks</h3>
//                         <p className="text-gray-100">
//                           Perfect for cooling down after a long game.
//                         </p>
//                       </div>
//                     </div>
//                   </div>

//                   {/* Two Bullet Points */}
//                   <ul className="list-disc list-inside space-y-2 text-gray-200 text-lg">
//                     <li>🎶 Enjoy live music while you eat and drink.</li>
//                     <li>🍹 Try our signature cocktails and craft beverages.</li>
//                     <li>🎮 Play exciting games and challenge your friends.</li>
//                   </ul>

//                   {/* Call to Action Button */}
//                   {/* <button className="inline-flex items-center px-8 py-4 bg-white text-primaryBtnColor font-heading font-bold rounded-2xl hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl">
//                     SEE OUR MENU
//                     <span className="ml-3 text-lg">→</span>
//                   </button> */}

//                   {/* Optional Footer / Extra Note */}
//                   <p className="mt-6 text-gray-200 text-sm">
//                     *Daily specials available. Ask our staff for today's feature
//                     items!
//                   </p>
//                 </div>
//               </div>

//               {/* Eat Drink Play Section */}
//               <div
//                 className="bg-white rounded-3xl p-8 border-2 border-gray-100 shadow-xl h-full overflow-hidden"
//                 data-aos="fade-left"
//               >
//                 <div className="flex flex-col h-full">
//                   {/* Image Section */}
//                   <div className="relative h-48 mb-6 rounded-2xl overflow-hidden group">
//                     <img
//                       src={EatDrinkPlayImage}
//                       alt="Eat Drink Play Experience"
//                       className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
//                     />
//                     <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
//                     <div className="absolute bottom-4 left-4">
//                       <span className="text-white text-sm font-semibold bg-primaryBtnHoverColor px-3 py-1 rounded-full">
//                         🎯 EXPERIENCE
//                       </span>
//                     </div>
//                   </div>

//                   <div className="flex items-center mb-6">
//                     <div className="w-16 h-16 bg-gradient-to-br from-primaryBtnColor to-primaryBtnHoverColor rounded-2xl flex items-center justify-center mr-6">
//                       <span className="text-3xl text-white">🎯</span>
//                     </div>
//                     <h2 className="text-3xl font-bold font-heading text-primaryTextColor">
//                       EAT, DRINK, PLAY!
//                     </h2>
//                   </div>
//                   <p className="text-lg text-secondaryTextColor leading-relaxed flex-grow">
//                     Our full-service restaurant and bar is the best place to sit
//                     back and relax before and after your games here at
//                     Silverbacks Indoor. With a wide variety of foods and
//                     beverages, we cater to everyone that walks through our
//                     doors.
//                   </p>
//                   <div className="mt-6 flex flex-wrap gap-3">
//                     <span className="px-4 py-2 bg-gray-50 text-primaryTextColor rounded-full text-sm font-semibold">
//                       🍔 Food
//                     </span>
//                     <span className="px-4 py-2 bg-gray-50 text-primaryTextColor rounded-full text-sm font-semibold">
//                       🍺 Drinks
//                     </span>
//                     <span className="px-4 py-2 bg-gray-50 text-primaryTextColor rounded-full text-sm font-semibold">
//                       🎮 Games
//                     </span>
//                   </div>
//                 </div>
//               </div>

//               {/* Big Screen Section */}
//               <div className="lg:col-span-3" data-aos="fade-up">
//                 <div className="relative rounded-3xl overflow-hidden shadow-2xl">
//                   {/* Background Image with Overlay */}
//                   <div className="absolute inset-0">
//                     <img
//                       src={BigScreenImage}
//                       alt="Big Screen Sports Viewing"
//                       className="w-full h-full object-cover"
//                     />
//                     <div className="absolute inset-0 bg-gradient-to-r from-primaryBtnColor/90 to-primaryBtnHoverColor/80"></div>
//                   </div>

//                   {/* Content */}
//                   <div className="relative z-10 p-8 lg:p-12 text-white">
//                     <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
//                       {/* Text Content */}
//                       <div>
//                         <div className="flex items-center mb-6">
//                           <div className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center mr-6 backdrop-blur-sm border border-white/30">
//                             <span className="text-4xl">📺</span>
//                           </div>
//                           <div>
//                             <h2 className="text-3xl lg:text-4xl font-bold font-heading mb-2">
//                               WATCH ON THE
//                               <br />
//                               <span className="bg-gradient-to-r from-white to-amber-200 bg-clip-text text-transparent">
//                                 BIG SCREEN!
//                               </span>
//                             </h2>
//                             <div className="w-24 h-1 bg-amber-400 rounded-full"></div>
//                           </div>
//                         </div>

//                         <p className="text-xl text-gray-100 mb-8 leading-relaxed">
//                           Banana's hosts a variety of events at Silverbacks
//                           Indoor, including watch-parties and much more. When we
//                           say big screen, we really mean it. Come and enjoy your
//                           favorite sports team make its way to the top.
//                         </p>

//                         {/* Features Grid */}
//                         <div className="grid grid-cols-3 gap-3 mb-6 max-w-full">
//                           <div className="text-center bg-white/10 rounded-xl p-3 sm:p-4 backdrop-blur-sm border border-white/20 flex flex-col items-center justify-center">
//                             <div className="text-base sm:text-xl md:text-2xl font-bold text-amber-300 leading-tight">
//                               100"
//                             </div>
//                             <div className="text-gray-200 text-xs sm:text-sm md:text-base font-semibold leading-tight whitespace-normal">
//                               SCREENS
//                             </div>
//                           </div>

//                           <div className="text-center bg-white/10 rounded-xl p-3 sm:p-4 backdrop-blur-sm border border-white/20 flex flex-col items-center justify-center">
//                             <div className="text-base sm:text-xl md:text-2xl font-bold text-amber-300 leading-tight">
//                               4K
//                             </div>
//                             <div className="text-gray-200 text-xs sm:text-sm md:text-base font-semibold leading-tight whitespace-normal">
//                               RESOLUTION
//                             </div>
//                           </div>

//                           <div className="text-center bg-white/10 rounded-xl p-3 sm:p-4 backdrop-blur-sm border border-white/20 flex flex-col items-center justify-center">
//                             <div className="text-base sm:text-xl md:text-2xl font-bold text-amber-300 leading-tight">
//                               Dolby
//                             </div>
//                             <div className="text-gray-200 text-xs sm:text-sm md:text-base font-semibold leading-tight whitespace-normal">
//                               AUDIO
//                             </div>
//                           </div>
//                         </div>

//                         {/* CTA Button */}
//                         <button className="inline-flex items-center px-8 py-4 bg-white text-primaryBtnColor font-heading font-bold rounded-2xl hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl">
//                           BOOK WATCH PARTY
//                           <span className="ml-3 text-lg">🎉</span>
//                         </button>
//                       </div>

//                       {/* Stats Card */}
//                       <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
//                         <h3 className="text-2xl font-bold text-center mb-6 text-amber-300">
//                           PREMIUM EXPERIENCE
//                         </h3>

//                         <div className="space-y-4">
//                           <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
//                             <span className="text-gray-200">Screen Size</span>
//                             <span className="font-bold text-amber-300">
//                               100" 4K
//                             </span>
//                           </div>

//                           <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
//                             <span className="text-gray-200">Audio System</span>
//                             <span className="font-bold text-amber-300">
//                               Dolby Atmos
//                             </span>
//                           </div>

//                           <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
//                             <span className="text-gray-200">
//                               Seating Capacity
//                             </span>
//                             <span className="font-bold text-amber-300">
//                               50+ People
//                             </span>
//                           </div>

//                           <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
//                             <span className="text-gray-200">Event Types</span>
//                             <span className="font-bold text-amber-300">
//                               All Sports
//                             </span>
//                           </div>
//                         </div>

//                         {/* Badge */}
//                         <div className="mt-6 text-center">
//                           <div className="inline-flex items-center px-4 py-2 bg-amber-500 text-white rounded-full text-sm font-bold">
//                             ⭐ ULTIMATE VIEWING EXPERIENCE
//                           </div>
//                         </div>
//                       </div>
//                     </div>

//                     {/* Bottom Features */}
//                     <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
//                       <div className="bg-white/5 rounded-lg p-3 backdrop-blur-sm border border-white/10">
//                         <div className="text-amber-300 text-lg">🎯</div>
//                         <div className="text-white text-sm font-semibold">
//                           HD Quality
//                         </div>
//                       </div>
//                       <div className="bg-white/5 rounded-lg p-3 backdrop-blur-sm border border-white/10">
//                         <div className="text-amber-300 text-lg">🔊</div>
//                         <div className="text-white text-sm font-semibold">
//                           Surround Sound
//                         </div>
//                       </div>
//                       <div className="bg-white/5 rounded-lg p-3 backdrop-blur-sm border border-white/10">
//                         <div className="text-amber-300 text-lg">🛋️</div>
//                         <div className="text-white text-sm font-semibold">
//                           Comfort Seating
//                         </div>
//                       </div>
//                       <div className="bg-white/5 rounded-lg p-3 backdrop-blur-sm border border-white/10">
//                         <div className="text-amber-300 text-lg">🎮</div>
//                         <div className="text-white text-sm font-semibold">
//                           Multi-View
//                         </div>
//                       </div>
//                     </div>
//                   </div>
//                 </div>
//               </div>
//             </div>

//             {/* Menu Section */}
//             <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
//               {/* Faith Barbeque Menu */}
//               <div className="group relative" data-aos="fade-right">
//                 <div className="relative bg-gradient-to-br from-primaryBtnColor to-primaryTextColor rounded-3xl p-1 shadow-2xl overflow-hidden">
//                   {/* Background Pattern */}
//                   <div className="absolute inset-0 opacity-10">
//                     <div
//                       className="absolute inset-0 bg-repeat"
//                       style={{
//                         backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.2'%3E%3Cpath d='M0 0h40v40H0V0zm20 20h20v20H20V20zm0-20h20v20H20V0z'/%3E%3C/g%3E%3C/svg%3E")`,
//                       }}
//                     ></div>
//                   </div>

//                   <div className="relative bg-white rounded-2xl overflow-hidden group-hover:scale-[1.02] transition-all duration-500">
//                     {/* Image Container */}
//                     <div className="relative h-56 overflow-hidden">
//                       <img
//                         src={BBQImage}
//                         alt="Faith Barbeque Menu"
//                         className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
//                       />
//                       {/* Gradient Overlay */}
//                       <div className="absolute inset-0 bg-gradient-to-t from-primaryBtnColor/80 to-transparent"></div>
//                       {/* Badge */}
//                       <div className="absolute top-4 left-4">
//                         <span className="bg-primaryBtnHoverColor text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg">
//                           🔥 SIGNATURE BBQ
//                         </span>
//                       </div>
//                       {/* Icon */}
//                       <div className="absolute top-4 right-4 w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
//                         <span className="text-2xl">🍖</span>
//                       </div>
//                     </div>

//                     {/* Content */}
//                     <div className="p-6 text-center">
//                       <h3 className="text-2xl lg:text-3xl font-bold font-heading text-primaryTextColor mb-3">
//                         Faith Barbeque
//                       </h3>
//                       <p className="text-secondaryTextColor mb-4 leading-relaxed">
//                         Slow-smoked specialties & mouth-watering grilled
//                         favorites
//                       </p>

//                       {/* Features */}
//                       <div className="flex justify-center space-x-3 mb-6">
//                         <span className="text-xs bg-amber-100 text-amber-800 px-3 py-1 rounded-full font-semibold">
//                           Smoked Meats
//                         </span>
//                         <span className="text-xs bg-amber-100 text-amber-800 px-3 py-1 rounded-full font-semibold">
//                           House Rubs
//                         </span>
//                         <span className="text-xs bg-amber-100 text-amber-800 px-3 py-1 rounded-full font-semibold">
//                           Fresh Sides
//                         </span>
//                       </div>

//                       <button
//                         onClick={() => setModalImage(BBQImage)}
//                         className="w-full py-4 bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor text-white font-heading font-bold rounded-xl hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl relative overflow-hidden group"
//                       >
//                         <span className="relative z-10">VIEW BBQ MENU</span>
//                         <div className="absolute inset-0 bg-gradient-to-r from-primaryBtnHoverColor to-red-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
//                       </button>
//                     </div>
//                   </div>
//                 </div>
//               </div>

//               {/* Full Menu */}
//               <div className="group relative" data-aos="fade-left">
//                 <div className="relative bg-gradient-to-br from-primaryBtnHoverColor to-red-600 rounded-3xl p-1 shadow-2xl overflow-hidden">
//                   {/* Background Pattern */}
//                   <div className="absolute inset-0 opacity-10">
//                     <div
//                       className="absolute inset-0 bg-repeat"
//                       style={{
//                         backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.2'%3E%3Cpath d='M0 0h40v40H0V0zm20 20h20v20H20V20zm0-20h20v20H20V0z'/%3E%3C/g%3E%3C/svg%3E")`,
//                       }}
//                     ></div>
//                   </div>

//                   <div className="relative bg-white rounded-2xl overflow-hidden group-hover:scale-[1.02] transition-all duration-500">
//                     {/* Image Container */}
//                     <div className="relative h-56 overflow-hidden">
//                       <img
//                         src={FullMenuImage}
//                         alt="Full Menu"
//                         className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
//                       />
//                       {/* Gradient Overlay */}
//                       <div className="absolute inset-0 bg-gradient-to-t from-primaryBtnHoverColor/80 to-transparent"></div>
//                       {/* Badge */}
//                       <div className="absolute top-4 left-4">
//                         <span className="bg-primaryBtnColor text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg">
//                           📚 COMPLETE MENU
//                         </span>
//                       </div>
//                       {/* Icon */}
//                       <div className="absolute top-4 right-4 w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
//                         <span className="text-2xl">🍽️</span>
//                       </div>
//                     </div>

//                     {/* Content */}
//                     <div className="p-6 text-center">
//                       <h3 className="text-2xl lg:text-3xl font-bold font-heading text-primaryTextColor mb-3">
//                         Full Menu
//                       </h3>
//                       <p className="text-secondaryTextColor mb-4 leading-relaxed">
//                         Complete selection of food, drinks, and daily specials
//                       </p>

//                       {/* Features */}
//                       <div className="flex justify-center space-x-3 mb-6">
//                         <span className="text-xs bg-red-100 text-red-800 px-3 py-1 rounded-full font-semibold">
//                           Burgers
//                         </span>
//                         <span className="text-xs bg-red-100 text-red-800 px-3 py-1 rounded-full font-semibold">
//                           Drinks
//                         </span>
//                         <span className="text-xs bg-red-100 text-red-800 px-3 py-1 rounded-full font-semibold">
//                           Desserts
//                         </span>
//                       </div>

//                       <button
//                         onClick={() => setModalImage(FullMenuImage)}
//                         className="w-full py-4 bg-gradient-to-r from-primaryBtnHoverColor to-red-600 text-white font-heading font-bold rounded-xl hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl relative overflow-hidden group"
//                       >
//                         <span className="relative z-10">VIEW FULL MENU</span>
//                         <div className="absolute inset-0 bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
//                       </button>
//                     </div>
//                   </div>
//                 </div>
//               </div>
//             </div>

//             {/* Modal */}
//             {modalImage && (
//               <div
//                 onClick={() => setModalImage(null)}
//                 className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-50 cursor-pointer p-4"
//               >
//                 <div className="relative max-w-4xl max-h-[90vh]">
//                   <img
//                     src={modalImage}
//                     alt="Menu"
//                     className="max-h-[85vh] max-w-full rounded-2xl shadow-2xl object-contain"
//                   />
//                   {/* Close Button */}
//                   <button
//                     onClick={() => setModalImage(null)}
//                     className="absolute -top-4 -right-4 w-12 h-12 bg-primaryBtnHoverColor text-white rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform duration-200"
//                   >
//                     ✕
//                   </button>
//                   {/* Download Hint */}
//                   <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/50 text-white px-4 py-2 rounded-full text-sm backdrop-blur-sm">
//                     Click anywhere to close
//                   </div>
//                 </div>
//               </div>
//             )}

//             {/* Feature Icons */}
//             <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-20 max-w-5xl mx-auto">
//               <div className="text-center">
//                 <div className="w-16 h-16 bg-primaryBtnHoverColor rounded-full flex items-center justify-center mx-auto mb-3">
//                   <span className="text-2xl text-white">🍔</span>
//                 </div>
//                 <p className="font-semibold text-primaryTextColor">
//                   Fresh Food
//                 </p>
//               </div>
//               <div className="text-center">
//                 <div className="w-16 h-16 bg-primaryBtnHoverColor rounded-full flex items-center justify-center mx-auto mb-3">
//                   <span className="text-2xl text-white">🍺</span>
//                 </div>
//                 <p className="font-semibold text-primaryTextColor">Cold Beer</p>
//               </div>
//               <div className="text-center">
//                 <div className="w-16 h-16 bg-primaryBtnHoverColor rounded-full flex items-center justify-center mx-auto mb-3">
//                   <span className="text-2xl text-white">📺</span>
//                 </div>
//                 <p className="font-semibold text-primaryTextColor">
//                   Big Screens
//                 </p>
//               </div>
//               <div className="text-center">
//                 <div className="w-16 h-16 bg-primaryBtnHoverColor rounded-full flex items-center justify-center mx-auto mb-3">
//                   <span className="text-2xl text-white">🎉</span>
//                 </div>
//                 <p className="font-semibold text-primaryTextColor">
//                   Watch Parties
//                 </p>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default BarAndRestaurant;
